import React, { useEffect, useState } from 'react';
import { LuTriangleAlert, LuMapPinPlus, LuUserPlus, LuX } from 'react-icons/lu';
import api from '../../services/api';
import { formatDateISO } from '../../utils/dateUtils';

import { Segmentado } from '../ui/Pantalla';
import DateInput from '../ui/DateInput';

// Se acepta hasta 60 días adelante: un cliente nuevo se instala en la visita, que es a futuro
const maxInstalacion = () => { const d = new Date(); d.setDate(d.getDate() + 60); return formatDateISO(d); };

// Alta de clientes del Portal Empresa (21-oct-2026): de dónde viene el cliente, su código y los
// nombres parecidos. Lo usan el alta desde Clientes y el cliente nuevo del pedido guiado.
const INPUT = 'w-full h-11 px-3.5 rounded-xl bg-chip text-ink text-body outline-none placeholder:text-muted';
const ROTULO = 'text-label font-black uppercase tracking-widest text-muted';

export const ORIGENES = [{ id: 'INSTALACION', label: 'Instalación nueva' }, { id: 'LOTE', label: 'Del lote' }];

// Origen, código y fecha de instalación. c: { origen, codigo, instaladoEl }; set(k)(valor)
export function CodigoYOrigen({ c, set, editando = false }) {
    const [sugerido, setSugerido] = useState('');
    const lote = c.origen === 'LOTE';
    const fecha = c.instaladoEl || '';
    // El código que se va a poner si lo deja vacío (sale de la fecha de instalación)
    useEffect(() => {
        if (c.codigo || editando) return undefined;
        let vivo = true;
        api.get('/empresa/clientes/codigo-siguiente', { params: fecha ? { fecha } : {} })
            .then(r => vivo && setSugerido(r.data?.codigo || '')).catch(() => {});
        return () => { vivo = false; };
    }, [fecha, c.codigo, editando]);
    return (
        <div className="space-y-2">
            <Segmentado valor={c.origen || 'INSTALACION'} onChange={set('origen')} opciones={ORIGENES} />
            <div className="grid grid-cols-2 gap-2">
                <label className="space-y-1">
                    <span className={ROTULO}>Código</span>
                    <input className={INPUT} value={c.codigo || ''} onChange={e => set('codigo')(e.target.value)}
                        placeholder={lote ? 'Ej. 2134' : (sugerido ? `${sugerido} (auto)` : 'Automático')} aria-label="Código de cliente" />
                </label>
                <label className="space-y-1">
                    <span className={ROTULO}>{lote ? 'Alta' : 'Instalado el'}</span>
                    <DateInput className={INPUT} value={fecha} max={maxInstalacion()} onChange={set('instaladoEl')} />
                </label>
            </div>
            <p className="text-label text-muted px-1">
                {lote
                    ? 'Poné el número que ustedes le dieron al cliente.'
                    : 'Si lo dejás vacío, el código se arma con la fecha de instalación (ej. 211026; si ese día hay otro, 211026A). Para cargar una instalación vieja, poné su fecha.'}
            </p>
        </div>
    );
}

// Clientes con un nombre igual o parecido: antes de guardar, ¿es otra dirección de uno de ellos o
// un cliente distinto? decision: null | { tipo: 'otra', cliente } | { tipo: 'distinto' }
export function AvisoParecidos({ nombre, excluir, decision, onDecidir, onParecidos }) {
    const [lista, setLista] = useState([]);
    const n = (nombre || '').trim();
    useEffect(() => {
        if (n.length < 3) { setLista([]); onParecidos && onParecidos([]); return undefined; }
        let vivo = true;
        const t = setTimeout(() => {
            api.get('/empresa/clientes/parecidos', { params: { nombre: n, ...(excluir ? { excluir } : {}) } })
                .then(r => { if (!vivo) return; const l = Array.isArray(r.data) ? r.data : []; setLista(l); onParecidos && onParecidos(l); })
                .catch(() => {});
        }, 350);
        return () => { vivo = false; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [n, excluir]);

    if (decision) {
        return (
            <div className="flex items-center gap-2 p-2.5 rounded-xl bg-panel">
                {decision.tipo === 'otra' ? <LuMapPinPlus size={16} className="shrink-0 text-brand-red" /> : <LuUserPlus size={16} className="shrink-0 text-brand-red" />}
                <span className="flex-1 min-w-0 text-caption text-secondary">
                    {decision.tipo === 'otra'
                        ? <>Otra dirección de <b className="text-ink">{decision.cliente.codigo ? `${decision.cliente.codigo} - ` : ''}{decision.cliente.nombre}</b></>
                        : <>Cliente distinto, con su propio código</>}
                </span>
                <button type="button" onClick={() => onDecidir(null)} aria-label="Cambiar"
                    className="shrink-0 h-8 px-2.5 rounded-lg bg-chip text-label font-black text-secondary inline-flex items-center gap-1"><LuX size={13} /> Cambiar</button>
            </div>
        );
    }
    if (!lista.length) return null;
    return (
        <div className="p-3 rounded-xl border-2 border-[#D97706]/40 bg-[rgba(217,119,6,0.08)] space-y-2">
            <p className="text-caption font-black text-ink inline-flex items-center gap-1.5">
                <LuTriangleAlert size={15} className="text-[#D97706]" />
                {lista.some(x => x.igual) ? 'Ya tenés un cliente con ese nombre' : 'Ya tenés un cliente con un nombre parecido'}
            </p>
            {lista.map(x => (
                <button key={x.id} type="button" onClick={() => onDecidir({ tipo: 'otra', cliente: x })}
                    className="w-full p-2.5 rounded-lg bg-card text-left active:scale-[0.99]">
                    <span className="block text-body font-black text-ink truncate">{x.codigo ? `${x.codigo} - ` : ''}{x.nombre}</span>
                    <span className="block text-caption text-muted truncate">{x.direcciones.filter(Boolean).join(' · ') || 'Sin dirección'}</span>
                    <span className="block text-label font-black text-brand-red mt-0.5">Es otra dirección de este cliente</span>
                </button>
            ))}
            <button type="button" onClick={() => onDecidir({ tipo: 'distinto' })}
                className="w-full h-10 rounded-lg bg-chip text-label font-black text-ink">Es otro cliente</button>
        </div>
    );
}
